import { Injectable, inject } from '@angular/core';
import { FeatureFlagService } from 'app/core/feature-flags/feature-flags.service';
import { JobPhaseRow, PhaseConfigApi, PhaseNameRow } from 'app/views/admin/phase-manager/phase-config-api';

/** Feature code that switches the rules-based phases on (Active Work Orders). */
export const PHASE_OVERLAY_FEATURE = 'WO_PHASE_MGR';

/** The overlay is applied to the jobs of this district only (the Active Work Orders screen). */
const OVERLAY_DISTRICT = '2';

/** Jobs per read command call. */
const CHUNK = 100;

/**
 * Adds the rules-based phase of a job (Phase Manager) to the job records Dash keeps in IndexedDB.
 *
 * It only ADDS properties (pmPhaseCode, pmSubCode, pmPhase, pmSubPhase, pmStale, pmVersionID); the legacy
 * phaseID / subPhaseID fields are never touched, so every other screen behaves as before. Any failure leaves
 * the jobs unchanged and the grid keeps showing the legacy phase.
 */
@Injectable({
  providedIn: 'root'
})
export class PhaseOverlayService {
  private readonly flags = inject(FeatureFlagService);
  private readonly api = inject(PhaseConfigApi);

  private phaseNames = new Map<string, string>();
  private subNames = new Map<string, string>();
  private namesVersion = 0;
  private warned = false;

  get enabled(): boolean {
    return this.flags.isEnabled(PHASE_OVERLAY_FEATURE);
  }

  /** Full load: reads the stored result of every job, without recalculating anything. */
  async enrichAll(jobs: any[]): Promise<void> {
    if (!this.enabled || !jobs?.length) {
      return;
    }
    try {
      const targets = jobs.filter(j => this.isTarget(j));
      const rows = await this.api.getJobPhases(null, false);
      await this.apply(targets, rows);
    } catch (e) {
      this.warn(e);
    }
  }

  /**
   * Copies the stored result of every job onto the given jobs (one call, nothing is recalculated).
   * Used when a list is opened, so a version published or applied in the meantime shows up.
   * Returns the jobs whose phase fields changed, so the caller can save them and refresh the grid.
   */
  async refreshStored(jobs: any[]): Promise<any[]> {
    if (!this.enabled || !jobs?.length) {
      return [];
    }
    try {
      const targets = jobs.filter(j => this.isTarget(j));
      const before = new Map<any, string>(targets.map(j => [j, this.signature(j)]));
      const rows = await this.api.getJobPhases(null, false);
      await this.apply(targets, rows);
      return targets.filter(j => this.signature(j) !== before.get(j));
    } catch (e) {
      this.warn(e);
      return [];
    }
  }

  /** Changed jobs (after an edit or another user's update): out-of-date results are recalculated by the server first. */
  async enrichChanged(jobs: any[]): Promise<void> {
    if (!this.enabled || !jobs?.length) {
      return;
    }
    try {
      const targets = jobs.filter(j => this.isTarget(j));
      const rows: JobPhaseRow[] = [];
      for (let i = 0; i < targets.length; i += CHUNK) {
        const ids = targets.slice(i, i + CHUNK).map(j => Number(j.jobID));
        rows.push(...(await this.api.getJobPhases(ids, true)));
      }
      await this.apply(targets, rows);
    } catch (e) {
      this.warn(e);
    }
  }

  private signature(job: any): string {
    return [job.pmPhaseCode, job.pmSubCode, job.pmPhase, job.pmSubPhase, job.pmStale, job.pmVersionID].join('|');
  }

  private isTarget(job: any): boolean {
    return !!job && job.jobID != null && String(job.districtID ?? '').trim() === OVERLAY_DISTRICT;
  }

  private async apply(targets: any[], rows: JobPhaseRow[]): Promise<void> {
    if (rows.length) {
      await this.ensureNames(Math.max(...rows.map(r => r.versionId)));
    }
    const byJob = new Map<number, JobPhaseRow>(rows.map(r => [r.jobId, r]));
    for (const job of targets) {
      const r = byJob.get(Number(job.jobID));
      if (!r || !r.phaseCode) {
        delete job.pmPhaseCode;
        delete job.pmSubCode;
        delete job.pmPhase;
        delete job.pmSubPhase;
        delete job.pmStale;
        delete job.pmVersionID;
        continue;
      }
      job.pmPhaseCode = r.phaseCode;
      job.pmSubCode = r.subCode;
      const district = this.districtOf(job);
      job.pmPhase = this.phaseLabel(district, r.phaseCode);
      job.pmSubPhase = r.subCode ? this.subLabel(district, r.phaseCode, r.subCode) : '';
      job.pmStale = r.stale;
      job.pmVersionID = r.versionId;
    }
  }

  /** District of a job: the same phase code means different phases in different districts. A missing district counts as 1. */
  private districtOf(job: any): number {
    const n = Number(String(job?.districtID ?? '').trim());
    return Number.isInteger(n) && n > 0 ? n : 1;
  }

  /** Same label format as the legacy grid: "1. Bidding" and "1a. Field Estimate". */
  private phaseLabel(district: number, phaseCode: string): string {
    const name = this.phaseNames.get(district + '|' + phaseCode);
    return name ? phaseCode + '. ' + name : phaseCode;
  }

  private subLabel(district: number, phaseCode: string, subCode: string): string {
    const name = this.subNames.get(district + '|' + phaseCode + '|' + subCode);
    return phaseCode + subCode + (name ? '. ' + name : '');
  }

  /** Loads the names again when a result from a newer configuration version shows up. */
  private async ensureNames(version: number): Promise<void> {
    if (this.namesVersion && version <= this.namesVersion) {
      return;
    }
    const rows: PhaseNameRow[] = await this.api.getNames();
    const phases = new Map<string, string>();
    const subs = new Map<string, string>();
    for (const r of rows) {
      if (r.kind === 'phase') {
        phases.set(r.district + '|' + r.phaseCode, r.name);
      } else if (r.subCode) {
        subs.set(r.district + '|' + r.phaseCode + '|' + r.subCode, r.name);
      }
    }
    this.phaseNames = phases;
    this.subNames = subs;
    this.namesVersion = rows.length ? Math.max(...rows.map(r => r.versionId)) : version;
  }

  private warn(e: any): void {
    if (!this.warned) {
      this.warned = true;
      console.warn('[PhaseOverlay] could not load the rules-based phases; the legacy phase is shown.', e);
    }
  }
}
